import { GridAlgorithm, MarkerClusterer } from '@googlemaps/markerclusterer';
import { useEffect, useState } from 'react';

const createNextMarkers = (markers) =>
  markers.map(({ position, listeners, id, icon }) => {
    const marker = new google.maps.Marker({ position, icon });

    if (listeners) {
      Object.keys(listeners).forEach((eventName) => {
        marker.addListener(eventName, (marker) =>
          listeners[eventName](id, marker)
        );
      });
    }

    return marker;
  });

// Adds or removes markers from a map.
const updateMapForMarkers = (markers, map = null) =>
  markers.forEach((marker) => marker.setMap(map));

function useMarkers(api, markers, map, clusterOptions) {
  const [renderedMarkers, setRenderedMarkers] = useState([]);
  const [markerClusterer, setMarkerClusterer] = useState(null);

  // Clustering.
  useEffect(() => {
    // If we've started clustering markers.
    if (clusterOptions && map && !markerClusterer) {
      const defaultClusterOptions = {
        markers: [],
        map,
        algorithm: new GridAlgorithm({}),
      };
      const cluster = new MarkerClusterer({
        ...defaultClusterOptions,
        ...clusterOptions,
      });

      // Need to manually set bounds because of a bug with setting map restrictions.
      // The first cluster click won't pan / zoom to the correct position without this.
      api.event.addListener(cluster, 'click', (cluster) => {
        map.fitBounds(cluster.bounds);
        map.setCenter(cluster.bounds.getCenter());
      });

      setMarkerClusterer(cluster);
    }

    // If we've stopped clustering markers.
    if (!clusterOptions && markerClusterer) {
      setMarkerClusterer(null);
    }
  }, [clusterOptions, map]);

  // Markers.
  useEffect(() => {
    if (map) {
      // Create a set of markers which will be rendered directly onto the map or be rendered by the marker clusterer.
      const nextMarkers = createNextMarkers(markers);

      // Render the new markers (clustered or directly) onto the map.
      if (clusterOptions) {
        if (markerClusterer) {
          try {
            markerClusterer.clearMarkers();
            markerClusterer.addMarkers(nextMarkers);
          } catch (e) {
            // eslint-disable-next-line no-console
            // console.error(e);
          }
        }
      } else {
        updateMapForMarkers(renderedMarkers, null);
        updateMapForMarkers(nextMarkers, map);
      }

      // Keep a reference to the latest set of rendered markers and cluster so we can update them when the set changes.
      setRenderedMarkers(nextMarkers);
    }
  }, [markers, map, clusterOptions, markerClusterer]);

  return renderedMarkers;
}

export default useMarkers;
